import { LoadingOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Modal, Row, Select, Spin, Upload } from "antd";
import TextArea from "antd/lib/input/TextArea";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ECapReportStepProps } from '.';
import { ReportTypeEnum, VALIDATION_MESSAGE_CONFIG } from "../../../../constants/constants";
import { MessageType } from '../../../../models/common/response-message';
import { EcapReportModel } from '../../../../models/ecap-report.model';
import { ECAPService } from '../../../../services/ecap.service';
import { DATE_UTIL } from "../../../../utils/dateUtils";
import UTIL from '../../../../utils/utils';
import { ClearIcon, InfoIcon, PaperIcon, PaperPlusIcon, UploadIcon } from "../../../atoms/icon";
import { showMessage } from '../../../atoms/notification/notification';
import InputTable from '../shared/input-table';
import CalculationProgress from './caculation-progress';

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const UploadFile = (props: ECapReportStepProps) => {

    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [fileLoadding, setFileLoading] = useState(false);
    const [file, setFile] = useState<File>();
    const [fileError, setFileError] = useState("");
    const [dataTable, setDataTable] = useState<any[]>();
    const data_file_ids = useRef<string[]>();

    const formSubmit = (values: EcapReportModel) => {
        if (!data_file_ids.current || !data_file_ids.current.length) {
            showMessage({
                message: "Please upload file!",
                type: MessageType.error
            });

            return;
        }
        values.data_file_ids = data_file_ids.current;

        if (props.next) {
            props.next(values, props.fileIndex);
        }
    }

    const dummyRequest = (options: any) => {
        setTimeout(() => {
            options.onSuccess("ok");
        }, 0);
    };

    const handleupload = (file: File) => {

        if (!form.getFieldValue("name")) {
            form.setFieldsValue({ name: UTIL.removeFileExtention(file.name) });
        }
        uploadFile(file);
        return "";
    }

    const clearFile = () => {
        setFile(undefined);
        setFileError("");
        setDataTable(undefined);
    }

    const confirmClearFile = () => {
        Modal.confirm({
            content: 'Do you want to delete this file?',
            onOk: clearFile,
            icon: '',
            width: 304
        });
    }

    const uploadFile = (file: File) => {
        if (file) {
            setFile(file);
            setFileError("");
            setDataTable(undefined);
            const values = { ...form.getFieldsValue() };

            values.file_index = props.fileIndex
            values.risk_type = props.risk_type;
            values.file = file;
            setFileLoading(true);

            ECAPService.uploadFile(values).then(res => {
                const uploadedData = res.data;
                data_file_ids.current = uploadedData.file_ids;
                setDataTable(uploadedData.input_data);

            }).catch(error => {
                setFileError(error?.response?.data?.message
                    ?? error?.response?.data?.suggestions ??
                    "Please retry the action. If the error persists contact your administrator");
            }).finally(() => {
                setFileLoading(false);
            });
        }
    }

    const getDownloadLink = () => {
        switch (props.risk_type) {
            case ReportTypeEnum.BusinessRisk:
                return props.resources?.sample_file_links?.br.BR_INPUT_DATA;
            case ReportTypeEnum.ConcentrationRisk:
                return props.resources?.sample_file_links?.conr.CONR_INPUT;
            case ReportTypeEnum.InterestRateRisk:
                return props.fileIndex === 1 ? props.resources?.sample_file_links?.irr.IRR_INPUT2 :
                    props.resources?.sample_file_links?.irr.IRR_INPUT1;
            case ReportTypeEnum.CreditRisk:
                return props.resources?.sample_file_links?.cr.CR_INPUT;
            case ReportTypeEnum.OperationalRisk:
                return props.resources?.sample_file_links?.or.OR_INPUT;
            case ReportTypeEnum.MarketRisk:
                return props.fileIndex === 1 ? props.resources?.sample_file_links?.mr.MR_INPUT1 :
                    props.resources?.sample_file_links?.mr.MR_INPUT2;
            case ReportTypeEnum.Ramp:
                return props.resources?.sample_file_links?.rapm.Portfolio_RAROC_INPUT;
            default:
                break;
        }
    }

    useEffect(() => {
        if (props.resources?.target_ratings && props.resources?.target_ratings.length) {
            form.setFieldsValue({ target_rating: props.resources?.target_ratings[0] });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.resources?.target_ratings])

    return (
        <React.Fragment>
            <div className="step-content-item-wrapper">

                {
                    props.caculating &&
                    <CalculationProgress />
                }

                {
                    !props.caculating &&

                    <React.Fragment>
                        <div className="step-content-item-header">
                            <PaperPlusIcon className="upload-header-icon" />
                            <h4>
                                {props.title}
                            </h4>
                            <p className="des-header">
                                {t("EcapModule.uploadFileDes", { param1: UTIL.getECapReportTypeDisplay(props.risk_type) })}&nbsp;
                                {t("EcapModule.lbClickDownload")}&nbsp;
                                <a className="text-underline" href={getDownloadLink()} download>
                                    {t("EcapModule.lbSampleFile")}
                                </a>
                            </p>

                            {
                                file && <div className={`file-item-wrapper${!!fileError ? " file-error" : ""}`}>
                                    <Spin spinning={fileLoadding} indicator={antIcon} >
                                        <div className="file-item">
                                            <div>
                                                <span>
                                                    <PaperIcon />
                                                    {file?.name}
                                                </span>
                                                <span className="mr-l-25">
                                                    {t("RapmReport.lbCsvFile")} &middot; {UTIL.bytesToSize(file?.size)} &middot; {t("RapmReport.lbAddedOn")} {DATE_UTIL.CurrentDate()}
                                                </span>
                                            </div>

                                            <Button shape="circle" className="clear-button" onClick={confirmClearFile}>
                                                <ClearIcon />
                                            </Button>
                                        </div>

                                    </Spin>
                                </div>
                            }

                            {
                                !!fileError && <span className="text-error">
                                    <span><InfoIcon /></span> <span>{fileError}</span>
                                </span>
                            }

                            <Upload
                                multiple={false}
                                action={handleupload}
                                maxCount={1}
                                listType="picture"
                                accept=".csv"
                                className="upload-file"
                                showUploadList={false}
                                customRequest={dummyRequest}>
                                {
                                    (!file || !!fileError) && <Button icon={<UploadIcon />} type="primary" shape="round">{(t("RapmReport.lbUploadFile"))}</Button>
                                }
                            </Upload>
                        </div>

                        <Form
                            layout='vertical'
                            validateMessages={VALIDATION_MESSAGE_CONFIG}
                            onFinish={(values) => formSubmit(values)}
                            scrollToFirstError
                            form={form}>

                            {
                                !props.fileIndex &&
                                <Form.Item
                                    label={t("EcapModule.lbTargetRating")}
                                    name="target_rating">
                                    <Select>
                                        {
                                            props.resources?.target_ratings && props.resources?.target_ratings?.map((item, index) => (
                                                <Select.Option value={item} key={index} >
                                                    {
                                                        item
                                                    }
                                                </Select.Option>
                                            ))
                                        }
                                    </Select>

                                </Form.Item>
                            }

                            <Form.Item
                                label={t("RapmReport.lbFileName")}
                                name="name">
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label={t("RapmReport.lbFileNotes")}
                                name="notes">
                                <TextArea rows={5} />
                            </Form.Item>

                            <Form.Item>

                                <Row gutter={8}>
                                    <Col span={12}>
                                        <Button type="default" onClick={props.goback} block shape="round">
                                            {t("Onboarding.btnGoback")}
                                        </Button>
                                    </Col>
                                    <Col span={12}>
                                        <Button htmlType="submit" type="primary" shape="round" block>
                                            {
                                                t("RapmReport.btnNext")
                                            }
                                        </Button>
                                    </Col>
                                </Row>

                            </Form.Item>

                        </Form>
                    </React.Fragment>
                }

            </div>

            {
                (dataTable && file && !props.caculating) &&
                <InputTable data={dataTable} file={file} />
            }

        </React.Fragment>
    )
}

export default UploadFile;